/**
 * Apple Pay / Google Pay buttons for the card checkout surfaces.
 *
 * Renders Stripe's Express Checkout Element (the official, brand-compliant wallet buttons) in its
 * own deferred-intent Elements group, turns the wallet authorisation into a card-type
 * PaymentMethod and hands its id back — callers then run their existing card pipeline unchanged.
 * Renders nothing when the browser/device has no supported wallet, or the amount can't be charged.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'next-themes';
import { Elements, ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type {
    StripeElementsOptionsMode,
    StripeExpressCheckoutElementClickEvent,
    StripeExpressCheckoutElementConfirmEvent,
    StripeExpressCheckoutElementOptions,
    StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';
import { stripePromise } from './StripeProvider';

// Stripe's minimum USD charge; below it the server grants directly and there is nothing to authorise.
const MIN_WALLET_AMOUNT_CENTS = 50;
const READY_TIMEOUT_MS = 15000;

export const walletPaymentsEnabled = import.meta.env.VITE_WALLET_PAYMENTS_ENABLED !== 'false';

export interface WalletBillingDetails {
    name?: string;
    email?: string;
    address: {
        line1?: string;
        city?: string;
        state?: string;
        postal_code?: string;
        country: string;
    };
}

export interface WalletRecurring {
    label: string;
    interval: 'month' | 'year';
    amountCents: number;
}

interface WalletPayButtonProps {
    /** Final, tax-inclusive amount the server will charge, in USD cents. */
    amountCents: number;
    mode?: 'payment' | 'subscription';
    /** Shown in the Apple Pay sheet as a recurring agreement (subscription mode only). */
    recurring?: WalletRecurring;
    /** The form's billing details — the server derives tax from the PaymentMethod's address, so it must match the previewed one. */
    billingDetails: WalletBillingDetails;
    disabled?: boolean;
    /** Return false to keep the wallet sheet closed (e.g. a form field became invalid). */
    canOpen?: () => boolean;
    /** Runs the caller's existing card pipeline with the wallet's PaymentMethod. */
    onPaymentMethod: (paymentMethodId: string) => Promise<void> | void;
    onError?: (message: string) => void;
    dividerLabel?: string;
}

export const WalletPayButton: React.FC<WalletPayButtonProps> = (props) => {
    const { amountCents, mode = 'payment' } = props;
    const roundedAmount = Math.round(amountCents);

    const elementsOptions = useMemo<StripeElementsOptionsMode>(() => ({
        mode,
        amount: roundedAmount,
        currency: 'usd',
        paymentMethodCreation: 'manual',
        paymentMethodTypes: ['card'],
    }), [mode, roundedAmount]);

    if (!walletPaymentsEnabled || !Number.isFinite(roundedAmount) || roundedAmount < MIN_WALLET_AMOUNT_CENTS) {
        return null;
    }

    return (
        // Keyed on mode: Elements can update amount in place, but not switch payment <-> subscription.
        <Elements key={mode} stripe={stripePromise} options={elementsOptions}>
            <WalletPayButtonInner {...props} />
        </Elements>
    );
};

const WalletPayButtonInner: React.FC<WalletPayButtonProps> = ({
    mode = 'payment', recurring, billingDetails, disabled, canOpen, onPaymentMethod, onError,
    dividerLabel = 'Or pay with card',
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const { resolvedTheme } = useTheme();
    const [available, setAvailable] = useState<boolean | null>(null);
    const isDark = resolvedTheme === 'dark';

    const options = useMemo<StripeExpressCheckoutElementOptions>(() => ({
        paymentMethods: {
            applePay: 'auto',
            googlePay: 'auto',
            link: 'never',
            paypal: 'never',
            amazonPay: 'never',
            klarna: 'never',
        },
        buttonType: {
            applePay: mode === 'subscription' ? 'subscribe' : 'buy',
            googlePay: mode === 'subscription' ? 'subscribe' : 'buy',
        },
        buttonTheme: {
            applePay: isDark ? 'white' : 'black',
            googlePay: isDark ? 'white' : 'black',
        },
        buttonHeight: 48,
        // Stripe rejects overflow 'never' unless maxRows is 0, and a rejected element never renders.
        layout: { maxColumns: 2, maxRows: 1, overflow: 'auto' },
    }), [mode, isDark]);

    const handleReady = useCallback((event: StripeExpressCheckoutElementReadyEvent) => {
        const methods = event.availablePaymentMethods;
        setAvailable(!!methods && (methods.applePay || methods.googlePay));
    }, []);

    const handleLoadError = useCallback((event: { error: { message?: string } }) => {
        console.warn('[WalletPayButton] Express Checkout failed to load:', event.error?.message);
        setAvailable(false);
    }, []);

    // A misconfigured element can throw inside Stripe without firing ready or loaderror; don't hold its hidden slot forever.
    useEffect(() => {
        if (available !== null) return;
        const timer = setTimeout(() => {
            console.warn('[WalletPayButton] Express Checkout never became ready; hiding wallet buttons');
            setAvailable(false);
        }, READY_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [available]);

    const handleClick = useCallback((event: StripeExpressCheckoutElementClickEvent) => {
        // resolve/reject must land within 1s of the click, so only synchronous checks belong here.
        if (disabled || (canOpen && !canOpen())) {
            event.reject();
            return;
        }
        if (mode === 'subscription' && recurring) {
            event.resolve({
                applePay: {
                    recurringPaymentRequest: {
                        paymentDescription: recurring.label,
                        managementURL: `${window.location.origin}/billing`,
                        regularBilling: {
                            label: recurring.label,
                            amount: Math.round(recurring.amountCents),
                            recurringPaymentIntervalUnit: recurring.interval,
                            recurringPaymentIntervalCount: 1,
                        },
                    },
                },
            });
            return;
        }
        event.resolve();
    }, [disabled, canOpen, mode, recurring]);

    const handleConfirm = useCallback(async (event: StripeExpressCheckoutElementConfirmEvent) => {
        if (!stripe || !elements) {
            event.paymentFailed({ reason: 'fail' });
            return;
        }
        const { error: submitError } = await elements.submit();
        if (submitError) {
            event.paymentFailed({ reason: 'fail' });
            onError?.(submitError.message || 'Payment could not be started');
            return;
        }
        const { address } = billingDetails;
        const { error, paymentMethod } = await stripe.createPaymentMethod({
            elements,
            params: {
                billing_details: {
                    // The form's address wins over the wallet's so tax matches what was previewed.
                    name: billingDetails.name?.trim() || event.billingDetails?.name || undefined,
                    email: billingDetails.email || event.billingDetails?.email || undefined,
                    address: {
                        line1: address.line1 || undefined,
                        city: address.city || undefined,
                        state: address.state || undefined,
                        postal_code: address.postal_code || undefined,
                        country: address.country,
                    },
                },
            },
        });
        if (error || !paymentMethod) {
            event.paymentFailed({ reason: 'fail' });
            onError?.(error?.message || 'Payment method could not be created');
            return;
        }
        await onPaymentMethod(paymentMethod.id);
    }, [stripe, elements, billingDetails, onPaymentMethod, onError]);

    if (available === false) return null;

    return (
        <div className="space-y-4">
            <div
                style={{ visibility: available ? 'visible' : 'hidden' }}
                className={disabled ? 'opacity-50 pointer-events-none' : undefined}
            >
                <ExpressCheckoutElement
                    options={options}
                    onReady={handleReady}
                    onLoadError={handleLoadError}
                    onClick={handleClick}
                    onConfirm={handleConfirm}
                />
            </div>
            {available && (
                <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-line" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{dividerLabel}</span>
                    <div className="h-px flex-1 bg-line" />
                </div>
            )}
        </div>
    );
};
